import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-breadcrumb.css?inline';

/**
 * `<pk-breadcrumb>` — breadcrumb trail.
 *
 * Three input modes (in priority order):
 *   1. **Explicit crumbs** — `crumbs` JSON attribute supplies an array of
 *      `{ label, href? }` items. The last item is rendered as the current
 *      page (no link).
 *   2. **Path-derived** — `path` attribute (or `window.location.pathname`)
 *      is split into title-cased segments. The slug-to-title transform
 *      sometimes mangles literal labels — see the `crumb` slot.
 *   3. **`crumb` slot** — when content is slotted, it replaces the
 *      trailing (current-page) crumb's text. Used when a hand-supplied
 *      label is more accurate than the slug-derived one.
 *
 * Attributes:
 *   - `path`       — explicit path to derive breadcrumb from (e.g. "/portal/systems")
 *   - `crumbs`     — JSON array: `[{"label":"Portal","href":"/portal"},…]`.
 *                    When set, overrides `path` entirely.
 *   - `root-label` — text for the first crumb (default "Portal")
 *   - `root-href`  — href for the first crumb (default "/portal")
 */
export class PkBreadcrumb extends HTMLElement {
    static readonly tagName = 'pk-breadcrumb';

    static get observedAttributes(): string[] {
        return ['path', 'crumbs', 'root-label', 'root-href'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    /**
     * Returns a crumb array: [{ label, href | null }]
     * The last crumb has href=null (current page).
     */
    private buildCrumbs(): Array<{ label: string; href: string | null }> {
        // 1) Explicit crumbs JSON wins.
        const crumbsAttr = this.getAttribute('crumbs');
        if (crumbsAttr) {
            try {
                const parsed = JSON.parse(crumbsAttr) as unknown;
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed.map((raw, idx, all) => {
                        const item = (raw ?? {}) as { label?: unknown; href?: unknown };
                        const label = typeof item.label === 'string' ? item.label : String(item.label ?? '');
                        const isLast = idx === all.length - 1;
                        const href = isLast
                            ? null
                            : (typeof item.href === 'string' ? item.href : null);
                        return { label, href };
                    });
                }
            } catch {
                // Fall through to path-derived crumbs.
            }
        }

        const rawPath = this.getAttribute('path') ??
            (typeof globalThis.window === 'undefined' ? '/portal' : globalThis.window.location.pathname);
        const rootLabel = this.getAttribute('root-label') ?? 'Portal';
        const rootHref  = this.getAttribute('root-href')  ?? '/portal';

        // Normalise: remove trailing slash, split
        const segments = rawPath.replace(/\/+$/, '').split('/').filter(Boolean);

        // Find the root segment to start from (skip before the root)
        // e.g. if rootHref="/portal" we start from segment "portal"
        const rootSegment = rootHref.replace(/\/+$/, '').split('/').findLast(Boolean) ?? '';
        const rootIndex   = segments.indexOf(rootSegment);
        const afterRoot   = rootIndex >= 0 ? segments.slice(rootIndex + 1) : segments;

        const crumbs: Array<{ label: string; href: string | null }> = [
            { label: rootLabel, href: afterRoot.length === 0 ? null : rootHref },
        ];

        afterRoot.forEach((seg, idx) => {
            const label = seg
                .replaceAll('-', ' ')
                .replaceAll(/\b\w/g, (c) => c.toUpperCase());
            const isLast = idx === afterRoot.length - 1;
            const href   = isLast
                ? null
                : rootHref + '/' + afterRoot.slice(0, idx + 1).join('/');
            crumbs.push({ label, href });
        });

        return crumbs;
    }

    private template(): TemplateResult {
        const crumbs = this.buildCrumbs();

        return html`
            <style>${styles}</style>
            <nav aria-label="Breadcrumb">
                <ol>
                    ${crumbs.map((crumb, idx) => {
                        const isLast = idx === crumbs.length - 1;
                        return html`
                            <li>
                                ${isLast
                                    ? html`<span aria-current="page" class="current">
                                            <slot name="crumb">${crumb.label}</slot>
                                        </span>`
                                    : html`<a href=${crumb.href!}>${crumb.label}</a>`
                                }
                                ${isLast ? '' : html`<span class="sep" aria-hidden="true">›</span>`}
                            </li>
                        `;
                    })}
                </ol>
            </nav>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkBreadcrumb.tagName)) {
    customElements.define(PkBreadcrumb.tagName, PkBreadcrumb);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-breadcrumb': PkBreadcrumb;
    }
}
